/* Displays the user's daily goal streak count in the nav bar.
   Shows the number of consecutive days all goals have been met. */

import { Flame, Zap, Trophy } from "lucide-react";
import { KiwiIcon } from "@/components/brand/KiwiIcon";
import { cn } from "@/lib/utils";

interface DailyStreakBadgeProps {
  streak: number;
  /** Whether today's goals are complete — controls active color vs muted. Defaults to streak > 0. */
  active?: boolean;
  variant?: "flame" | "bolt" | "trophy" | "kiwi" | "pill" | "minimal";
  className?: string;
}

export function DailyStreakBadge({
  streak,
  active,
  variant = "flame",
  className,
}: DailyStreakBadgeProps) {
  const isActive = active ?? streak > 0;

  if (variant === "flame") {
    return (
      <div className={cn("flex items-center gap-1", className)}>
        <Flame
          className={cn(
            "size-5",
            isActive ? "text-orange-500" : "text-muted-foreground/40",
          )}
        />
        <span
          className={cn(
            "text-sm font-bold tabular-nums",
            isActive ? "text-orange-500" : "text-muted-foreground/40",
          )}
        >
          {streak}
        </span>
      </div>
    );
  }

  if (variant === "bolt") {
    return (
      <div className={cn("flex items-center gap-1", className)}>
        <Zap
          className={cn(
            "size-5",
            streak > 0
              ? "text-yellow-500 fill-yellow-500"
              : "text-muted-foreground/40",
          )}
        />
        <span
          className={cn(
            "text-sm font-bold tabular-nums",
            isActive ? "text-yellow-500" : "text-muted-foreground/40",
          )}
        >
          {streak}
        </span>
      </div>
    );
  }

  if (variant === "trophy") {
    return (
      <div className={cn("flex items-center gap-1", className)}>
        <Trophy
          className={cn(
            "size-5",
            isActive ? "text-amber-500" : "text-muted-foreground/40",
          )}
        />
        <span
          className={cn(
            "text-sm font-bold tabular-nums",
            isActive ? "text-amber-500" : "text-muted-foreground/40",
          )}
        >
          {streak}
        </span>
      </div>
    );
  }

  if (variant === "kiwi") {
    return (
      <div className={cn("relative flex items-center gap-1", className)}>
        <div className="relative">
          <KiwiIcon
            className={cn(
              "size-6",
              isActive ? "text-primary" : "text-muted-foreground/40",
            )}
          />
          {isActive && (
            <span className="absolute -top-1.5 -right-2.5 text-[9px] font-bold text-orange-500 bg-background rounded-full size-4 flex items-center justify-center border border-orange-500/30">
              {streak}
            </span>
          )}
        </div>
        {!isActive && (
          <span className="text-sm font-bold tabular-nums text-muted-foreground/40">
            {streak}
          </span>
        )}
      </div>
    );
  }

  if (variant === "pill") {
    return (
      <div
        className={cn(
          "flex items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-bold tabular-nums",
          streak > 0
            ? "bg-orange-500/15 text-orange-500"
            : "bg-muted text-muted-foreground/40",
          className,
        )}
      >
        <Flame className="size-4" />
        <span>{streak}</span>
      </div>
    );
  }

  // minimal
  return (
    <div
      className={cn(
        "flex items-center gap-0.5 text-sm tabular-nums",
        isActive ? "text-orange-500" : "text-muted-foreground/40",
        className,
      )}
    >
      <span className="font-bold">{streak}</span>
      <Flame className="size-3.5" />
    </div>
  );
}
